import React from 'react';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Container from 'react-bootstrap/Container';
import ListGroup from 'react-bootstrap/ListGroup';
import Button from 'react-bootstrap/Button';
import Tabs from 'react-bootstrap/Tabs';
import Tab from 'react-bootstrap/Tab';

import ClientCreator from './ClientCreator.js';
import ClientDetails from './ClientDetails.js';
import Archive from './Archive.js';

import * as validation from '../util/Forms.js';
import {makeCountrySelection, makeGenderSelection} from '../util/Forms.js';
import {dateToDE} from "../util/Util";
import {Item} from "../util/UtilComponents";
import {teamMemberFields} from "./TeamEditor";

/*
Root element for rendering a Client. Depending on what action is being taken (view, update, create) a sub-element is
rendered.
Also includes a lot of util functions like labelers and schema information for parsing updates.
 */
const Client = ({
	// data
	client,

	// state
	action,

	// action changes
	startEditing,
	cancelEditing,
	cancelCreating,

	// execution
	create,
	update,
	remove,

	// analyses
	products,
	productData,
	createAnalysis,
	selectAnalysis,
	selectKairogramm,

	// questionnaire skeleton JSON
	issueQuestionnaire,
	checkForQuestionnaire,
	deleteNewestQuestionnaire,

	// info display
	showInfo,
}) => {
	const header = (
		<>
			<Col>
				<h3 className = 'kairos-client-header'>
					{action === 'create' ? 'Neuer Klient' : `${client.surname}, ${client.name}`}
				</h3>
			</Col>
			{!action &&
			<Col xs='auto'>
				{startEditing &&
				<Button className = {'mr-1'}
					onClick = {startEditing}>
					<i className = {'bi-pencil'}/>
				</Button>}
				{remove &&
				<Button className = {'btn-danger ml-1'}
					onClick = {() => remove(client.id)}>
					<i className = {'bi-x-lg'}/>
				</Button>}
			</Col>}
		</>
	);

	const handleIssueQuestionnaire = type => issueQuestionnaire(client.id, type);

	const hasAnyAnalyses = client.id && analysisFields.find(f => client[f].length);

	let content;

	// key because the Creator isn't dismounted when switching between edit and create

	switch (action) {
		case 'edit': {
			content = (
				<ClientCreator
					key = {'edit'}
					client = {client}
					execute = {updatedClient => update(createClientUpdate(client, updatedClient))}
					cancel = {cancelEditing}/>
			);
			break;
		};
		case 'create': {
			content = (
				<ClientCreator
					key = {'create'}
					client = {client}
					execute = {create}
					cancel = {cancelCreating}/>
			);
			break;
		};
		default: {
			content = (
				<Tabs
					defaultActiveKey = {'details'}>
					<Tab
						title = {'Klient'}
						eventKey = {'details'}>
						<ClientDetails
							products = {products.filter(p => p.teamAnalysis === (client.team != null))}
							productData = {productData}
							createAnalysis = {createAnalysis}
							selectAnalysis = {selectAnalysis}
							selectKairogramm = {selectKairogramm}
							issueQuestionnaire = {handleIssueQuestionnaire}
							checkForQuestionnaire = {checkForQuestionnaire}
							deleteNewestQuestionnaire = {deleteNewestQuestionnaire}
							client = {client}
							showInfo = {showInfo}/>
					</Tab>
					{hasAnyAnalyses &&
					<Tab
						title = {'Archiv'}
						eventKey = {'archive'}>
						<Archive
							client = {client}
							selectAnalysis = {selectAnalysis}
							selectKairogramm = {selectKairogramm}/>
					</Tab>}
				</Tabs>
			);
			break;
		};
	};



	// render

	return (
		<>
			<Row className = {'mb-2'}>
				{header}
			</Row>
			<Row>
				<Col>
					{content}
				</Col>
			</Row>
		</>
	);
};

export default Client;

export const new_client = {
	country: '',
	clients: [ ],
	person: {
		gender: 'MALE',
	},
};

export const createClientUpdate = (client, edited) => {
	const update = { };

	// country requires special UI but handling is the same
	fields
		.map(f => f.fieldname)
		.forEach(f => {
			if (edited[f] !== client[f])
				update[f] = edited[f];
	});

	// team
	if (client.team) {
		// prepare team-update
		update.teamUpdate = {
			updatedMembers: { },
		};

		// teamname
		if (edited.team.name != client.team.name)
			update.teamUpdate.name = edited.team.name;

		// company
		if (edited.team.company != client.team.company)
			update.teamUpdate.company = edited.team.company;

		// added: without ID
		update.teamUpdate.addedMembers = edited.team.members.filter(m => !m.id);

		// removed: IDs don't appear in edited
		update.teamUpdate.removedMembers = client.team.members
				.filter(m => !edited.team.members
						.map(em => em.id)
						.some(emId => emId === m.id));

		// potentially updated: IDs in both
		client.team.members.forEach(member => {
			const editedMember = edited.team.members.find(em => em.id === member.id);
			if (editedMember) {
				let includeUpdate = false;
				const memberUpdate = { };
				teamMemberFields
					.map(f => f.fieldname)
					.forEach(f => {
					if (editedMember[f] !== member[f]) {
						memberUpdate[f] = editedMember[f];
						includeUpdate = true;
					}
				});

				if (includeUpdate)
					update.teamUpdate.updatedMembers[member.id] = memberUpdate;
			}
		});

		// focus person
		update.teamUpdate.focusPerson = edited.team.focusPersonIdx;
	}
	// person
	else {
		update.personUpdate = { };
		personFields
			.map(f => f.fieldname)
			.forEach(f => {
				if (edited.person[f] !== client.person[f])
					update.personUpdate[f] = edited.person[f];
			})
		// if (edited.person.birthday !== client.person.birthday)
		// 	update.birthday = edited.person.birthday;
	}

	return update;
};

export const labeler = c => `${c.surname}, ${c.name}${c.team ? ` [${c.team.name}]` : ''}`;

export const fields = [
	{
		label: 'Vorname',
		fieldname: 'name',
		required: true,
		pattern: validation.name.pattern,
		validationMessage: validation.name.message,
	},
	{
		label: 'Nachname',
		fieldname: 'surname',
		required: true,
		pattern: validation.surname.pattern,
		validationMessage: validation.surname.message,
	},
	{
		label: 'Straße',
		fieldname: 'street',
		required: false,
		pattern: validation.street.pattern,
		validationMessage: validation.street.message,
	},
	{
		label: 'Hausnummer',
		fieldname: 'houseNumber',
		required: false,
		pattern: validation.houseNumber.pattern,
		validationMessage: validation.houseNumber.message,
	},
	{
		label: 'PLZ',
		fieldname: 'zipcode',
		required: false,
		pattern: validation.zipCode.pattern,
		validationMessage: validation.zipCode.message,
	},
	{
		label: 'Ort',
		fieldname: 'city',
		required: false,
		pattern: validation.city.pattern,
		validationMessage: validation.city.message,
	},
	{
		label: 'Telefon',
		fieldname: 'phoneNumber',
		required: false,
		pattern: validation.phoneNumber.pattern,
		validationMessage: validation.phoneNumber.message,
	},
	{
		label: 'E-Mail-Adresse',
		fieldname: 'emailAddress',
		required: true,
		pattern: validation.emailAddress.pattern,
		validationMessage: validation.emailAddress.message,
	},
	{
		label: 'Notiz',
		fieldname: 'note',
		as: 'textarea',
	},
	makeCountrySelection(false),
];

export const personFields = [{
		label: 'Geb.',
		fieldname: 'birthday',
		required: true,
		type: 'date',
		extract: client => client.person.birthday,
	},
	{
		label: 'Geburtsort',
		fieldname: 'birthplace',
		required: false,
		pattern: validation.city.pattern,
		validationMessage: validation.city.message,
		extract: client => client.person.birthplace,
	},
	makeGenderSelection(client => client.person.gender),
	{
		label: 'Vater',
		fieldname: 'nameOfFather',
		required: false,
		pattern: validation.name.pattern,
		validationMessage: validation.name.message,
		extract: client => client.person.nameOfFather,
	},
	{
		label: 'Mutter',
		fieldname: 'nameOfMother',
		required: false,
		pattern: validation.name.pattern,
		validationMessage: validation.name.message,
		extract: client => client.person.nameOfMother,
	},
	{
		label: 'Geschwister',
		fieldname: 'siblings',
		required: false,
		extract: client => client.person.siblings,
	},
	{
		label: 'Partner',
		fieldname: 'nameOfPartner',
		required: false,
		pattern: validation.name.pattern,
		validationMessage: validation.name.message,
		extract: client => client.person.nameOfPartner,
	},
	{
		label: 'Firma',
		fieldname: 'company',
		required: false,
		extract: client => client.person.company,
	},
	{
		label: 'Funktion',
		fieldname: 'function',
		required: false,
		extract: client => client.person.function,
	},
	{
		label: 'Vita',
		fieldname: 'vita',
		required: false,
		extract: client => client.person.vita,
	},
];

export const analysisFields = [
	'kairogramme',
	'standardEinzelanalysen',
	'premiumEinzelKairosanalysen',
	'standortBestimmungen',
	'kairosKalender',
	'teamKairosanalysen',
	'persoenlicheKairosanalysen',
];

export const analysisNameToFieldMap = {
	KAIROGRAMM: 'kairogramme',
	STANDORTBESTIMMUNG: 'standortBestimmungen',
	STANDARD_EINZEL_KAIROSANALYSE: 'standardEinzelanalysen',
	PREMIUM_EINZEL_KAIROSANALYSE: 'premiumEinzelKairosanalysen',
	TEAM_KAIROSANALYSE: 'teamKairosanalysen',
	KAIROS_KALENDER: 'kairosKalender',
	PERSOENLICHE_KAIROSANALYSE: 'persoenlicheKairosanalysen'
};

export const questionnaireData = {
	Einzelanalyse: {
		fieldname: 'questionnaireEinzel',
		type: 'Einzelanalyse',
	},
	Teamanalyse: {
		fieldname: 'questionnaireStandortbestimmung',
		type: 'Teamanalyse',
	},
	Standortbestimmung: {
		fieldname: 'questionnaireStandortbestimmung',
		type: 'Standortbestimmung',
	},
};

export const toListGroupCompact = client => {
	let entries = [];
	if (client.person) {
		const born = `Geboren am ${dateToDE(client.person.birthday)} ${client.person.birthplace ? ` in ${client.person.birthplace}` : ''}`;
		entries.push((
			<ListGroup.Item
				key ={'born'}>
				<Container>
					<Row>
						<Col className = 'col-4'>
							{born}
						</Col>
					</Row>
				</Container>
			</ListGroup.Item>
		));
	}

	// email
	entries.push(
		<Item
		key = {'email'}
		label = {'E-Mail-Adresse'}
		value = {client.emailAddress ?
			(<a href={`mailto://${client.emailAddress}`}>
				{`${client.emailAddress}`}
			</a>)
			: ''}/>);

	// phone
	entries.push(
		<Item
			key = {'phone'}
			label = {'Telefon'}
			fieldname = {'phone'}
			value = {client.phoneNumber || ''}/>);

	// address
	{
		let address = '';

		const streetRow = (client.street ? `${client.street} ` : '') +
			`${client.houseNumber || ''}`;

		if (streetRow.length)
			address += streetRow;

		const cityRow = (client.zipcode ? `${client.zipcode} ` : '') +
			`${client.city || ''}`;

		if (cityRow.length) {
			if (address.length)
				address += '\n';

			address += cityRow;
		}

		const countryRow = client.country ? `${client.country || ''}` : '';

		if (countryRow.length) {
			if (address.length)
				address += '\n';

			address += countryRow;
		}

		entries.push(
			<Item
				key = {'address'}
				label = {'Adresse'}
				value = {address}
				valueClasses = {'multiline-text'}/>);
	}

	// personal data
	if (client.person) {
		// company + function
		const company = client.person.company;
		const func = client.person.function;
		/*
		nichts -> ''
		company -> company
		func -> 'unbekannt (func)'
		beides -> 'company (func)'
		 */

		let compAndFunc;
		if (!company && !func)
			compAndFunc = '';
		else {
			if (company)
				compAndFunc = `${company}${func ? ` (${func})` : ''}`;
			else
				compAndFunc = `unbekannt (${func})`;
		}
		entries.push(
			<Item
				key = {'company'}
				label = {'Firma'}
				value = {compAndFunc}/>);

		// vita
		entries.push(
			<Item
				key = {'vita'}
				label = {'Vita'}
				value = {client.person.vita}/>);

		// parents
		const father = client.person.nameOfFather;
		const mother = client.person.nameOfMother;
		const parents = (father && mother) ? `${father}, ${mother}` : father ? father : mother;
		entries.push(
			<Item
				key = {'parents'}
				label = {'Eltern'}
				value = {parents}/>);

		// siblings
		entries.push(
			<Item
				key = {'siblings'}
				label = {'Geschwister'}
				value = {client.person.siblings}/>);

		// partner
		entries.push(
			<Item
				key = {'nameOfPartner'}
				label = {'Partner'}
				value = {client.person.nameOfPartner}/>);
	}

	// note
	entries.push(
		<Item
			key = {'note'}
			label = {'Notiz'}
			value = {client.note}
			valueClasses = {'multiline-text'}
			labelTooltip = {'Die Notiz wird nicht in der Analyse verarbeitet'}/>
);

	return entries;
};
